import React, { Fragment, ReactNode } from 'react';
import { Grid, Skeleton, styled, Typography } from '@mui/material';
import { differenceInDays } from 'date-fns';

import { apiDateToLocalDate } from '../../utils/dateUtils';
import { ValueReferences } from '../../utils/enums';
import { formatNumber } from '../../utils/formattingUtils';
import { useFilters } from '../Filters/useFilters';

const classesPrefix = 'volume';

const classes = {
    orderVolumeContainer: `${classesPrefix}-orderVolumeContainer`,
    orderVolumeLabel: `${classesPrefix}-orderVolumeLabel`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`& .${classes.orderVolumeContainer}`]: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            flexDirection: 'row',
        },
        [`& .${classes.orderVolumeLabel}`]: {
            color: theme.palette.text.secondary,
            fontSize: theme.typography.h6.fontSize,
        },
    };
});

const Volume = ({
    loading,
    orderVolume,
    showDailyVolume,
}: {
    loading: boolean;
    orderVolume: number | null;
    showDailyVolume?: boolean;
}): React.JSX.Element => {
    const filters = useFilters();

    const days = differenceInDays(
        apiDateToLocalDate(filters.latest_delivery_departure_timestamp),
        apiDateToLocalDate(filters.earliest_delivery_departure_timestamp)
    );

    let volumeDisplay: ReactNode = ValueReferences.NA;
    let volumePerDay: ReactNode = ValueReferences.NA;

    if (orderVolume !== null) {
        volumeDisplay = (
            <Fragment>
                <span>{formatNumber(orderVolume, 0, 0)}</span>
                <span className={classes.orderVolumeLabel}> orders</span>
            </Fragment>
        );

        if (days) {
            const averageVolumePerDay = orderVolume / days;
            if (averageVolumePerDay < 1) {
                volumePerDay = <span>Less than 1 order/day</span>;
            } else {
                volumePerDay = <span>{`${formatNumber(averageVolumePerDay, 1, 1)} orders/day`}</span>;
            }
        } else {
            volumePerDay = `${formatNumber(orderVolume, 0, 0)} orders/day`;
        }
    }

    return (
        <StyledGrid container direction='column'>
            <Grid item xs='auto'>
                <Typography variant='h4'>Orders</Typography>
            </Grid>
            <Grid item xs='auto' className={classes.orderVolumeContainer}>
                <Typography variant='h3'>
                    {loading ? <Skeleton width={100} /> : volumeDisplay}
                </Typography>
            </Grid>
            <Grid item xs className={classes.orderVolumeContainer}>
                {
                    showDailyVolume &&
                    <Typography variant='subtitle1'>
                        {loading ? <Skeleton width={100} /> : volumePerDay}
                    </Typography>
                }
            </Grid>
        </StyledGrid>
    );
};

export default Volume;
